import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, Search, Filter, ChevronDown, ArrowDownCircle, ArrowUpCircle, RefreshCw } from 'lucide-react';
import { apiService } from '../../services/api';
import { MovimientoInventario } from '../../types';
import { format } from 'date-fns';

const TIPO_CONFIG: Record<string, { label: string; color: string; bgColor: string }> = {
  venta: { label: 'Venta', color: 'text-blue-700', bgColor: 'bg-blue-50' },
  entrada: { label: 'Entrada', color: 'text-green-700', bgColor: 'bg-green-50' },
  salida: { label: 'Salida', color: 'text-orange-700', bgColor: 'bg-orange-50' },
  merma: { label: 'Merma', color: 'text-red-700', bgColor: 'bg-red-50' },
  ajuste: { label: 'Ajuste', color: 'text-yellow-700', bgColor: 'bg-yellow-50' },
  consumo_personal: { label: 'Consumo Personal', color: 'text-purple-700', bgColor: 'bg-purple-50' },
};

const Movimientos: React.FC = () => {
  const [movimientos, setMovimientos] = useState<MovimientoInventario[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [tipoFilter, setTipoFilter] = useState<string>('all');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });

  const fetchMovimientos = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { limit: 500 };
      if (dateRange.from) params.startDate = dateRange.from;
      if (dateRange.to) params.endDate = dateRange.to;
      if (tipoFilter !== 'all') params.tipo = tipoFilter;

      const data = await apiService.getMovimientos(params);
      setMovimientos(data);
    } catch (error) {
      console.error('Error fetching movimientos:', error);
    } finally {
      setLoading(false);
    }
  }, [dateRange, tipoFilter]);

  useEffect(() => {
    fetchMovimientos();
  }, [fetchMovimientos]);

  const filtered = movimientos.filter(m => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      m.productoNombre.toLowerCase().includes(q) ||
      m.motivo.toLowerCase().includes(q) ||
      (m.referencia || '').toLowerCase().includes(q)
    );
  });

  // Summary calculations
  const today = new Date().toISOString().split('T')[0];
  const todayMovimientos = movimientos.filter(m => m.fecha.startsWith(today));
  const todayDeductions = todayMovimientos.filter(m => m.cantidad < 0).reduce((sum, m) => sum + m.cantidad, 0);
  const todayEntries = todayMovimientos.filter(m => m.cantidad > 0).reduce((sum, m) => sum + m.cantidad, 0);
  const todayNet = todayDeductions + todayEntries;

  const formatDate = (fecha: string) => {
    try {
      return format(new Date(fecha), 'dd/MM/yyyy HH:mm');
    } catch {
      return fecha;
    }
  };

  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-dark-text font-heading flex items-center">
            <TrendingUp size={28} className="mr-3 text-accent" />
            Movimientos de Inventario
          </h2>
          <p className="text-sm text-medium-gray mt-1">
            Registro de todos los cambios en el inventario
          </p>
        </div>
        <button
          onClick={fetchMovimientos}
          disabled={loading}
          className="flex items-center px-4 py-2 bg-accent text-cream rounded-lg hover:bg-warm-brown transition-colors disabled:opacity-50 mt-4 md:mt-0"
        >
          <RefreshCw size={18} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg p-4 shadow-sm border border-light-gray/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-medium-gray">Deducciones Hoy</p>
              <p className="text-2xl font-bold text-red-500">{todayDeductions.toFixed(1)}</p>
            </div>
            <ArrowDownCircle className="text-red-400" size={24} />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 shadow-sm border border-light-gray/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-medium-gray">Entradas Hoy</p>
              <p className="text-2xl font-bold text-green-600">+{todayEntries.toFixed(1)}</p>
            </div>
            <ArrowUpCircle className="text-green-400" size={24} />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 shadow-sm border border-light-gray/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-medium-gray">Cambio Neto Hoy</p>
              <p className={`text-2xl font-bold ${todayNet >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                {todayNet >= 0 ? '+' : ''}{todayNet.toFixed(1)}
              </p>
            </div>
            <TrendingUp className="text-accent" size={24} />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg p-4 shadow-sm border border-light-gray/20 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-medium-gray" />
            <input
              type="text"
              placeholder="Buscar producto, motivo..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-light-gray/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent text-sm"
            />
          </div>
          <div className="relative">
            <Filter size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-medium-gray pointer-events-none" />
            <select
              value={tipoFilter}
              onChange={(e) => setTipoFilter(e.target.value)}
              className="w-full pl-10 pr-10 py-2 border border-light-gray/30 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-accent appearance-none"
            >
              <option value="all">Todos los tipos</option>
              {Object.entries(TIPO_CONFIG).map(([key, cfg]) => (
                <option key={key} value={key}>{cfg.label}</option>
              ))}
            </select>
            <ChevronDown size={16} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-medium-gray pointer-events-none" />
          </div>
          <input
            type="date"
            value={dateRange.from}
            onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
            className="px-3 py-2 border border-light-gray/30 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent"
          />
          <input
            type="date"
            value={dateRange.to}
            onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
            className="px-3 py-2 border border-light-gray/30 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-light-gray/20 overflow-hidden">
        <div className="px-6 py-4 border-b border-light-gray/20 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-dark-text font-heading">
            Historial de Movimientos
          </h3>
          <span className="text-sm text-medium-gray">{filtered.length} registros</span>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto"></div>
            <p className="mt-4 text-medium-gray">Cargando movimientos...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-medium-gray">
            <TrendingUp size={48} className="mx-auto mb-4 opacity-30" />
            <p className="text-lg">Sin movimientos registrados</p>
            <p className="text-sm mt-1">Los movimientos se generan al procesar ventas o ajustar inventario</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-light-gray/20">
              <thead className="bg-cream">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-medium-gray uppercase">Fecha</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-medium-gray uppercase">Tipo</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-medium-gray uppercase">Producto</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-medium-gray uppercase">Cantidad</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-medium-gray uppercase">Stock</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-medium-gray uppercase">Motivo</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-medium-gray uppercase">Ref.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-light-gray/10">
                {filtered.map((mov) => {
                  const config = TIPO_CONFIG[mov.tipo] || { label: mov.tipo, color: 'text-gray-700', bgColor: 'bg-gray-50' };
                  return (
                    <tr key={mov._id} className="hover:bg-cream/30 transition-colors">
                      <td className="px-4 py-3 text-sm text-dark-text whitespace-nowrap">
                        {formatDate(mov.fecha)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${config.color} ${config.bgColor}`}>
                          {config.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-dark-text">
                        {mov.productoNombre}
                      </td>
                      <td className={`px-4 py-3 text-sm font-medium text-right ${mov.cantidad < 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {mov.cantidad > 0 ? '+' : ''}{mov.cantidad} {mov.unidad}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-xs text-medium-gray">
                          {mov.stockAnterior} &rarr;{' '}
                          <span className="font-medium text-dark-text">{mov.stockNuevo}</span>
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-medium-gray max-w-[250px] truncate" title={mov.motivo}>
                        {mov.motivo}
                      </td>
                      <td className="px-4 py-3 text-xs text-medium-gray font-mono">
                        {mov.referencia || '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Movimientos;
